import { setActivePinia } from 'pinia'
import { navigateTo, useNuxtApp } from 'nuxt/app'

import {
  clearAdminPreviewAgencyToken,
  clearAdminV2PreviewEnvelope,
  getAdminV2PreviewLocationId,
  readAdminPreviewAgencyToken,
  readAdminV2PreviewEnvelope,
} from '@common/core-layer/utils/admin-preview-token.utils'
import { clearAdminPreviewMarkerCookie } from '@common/core-layer/utils/admin-preview-cookie.utils'

import {
  isCoursesAdminPreviewRoute,
  isV2PreviewToken,
  mergeCoursesAdminPreviewQuery,
  parseCoursesAdminPreviewContextFromQuery,
  parseCoursesAdminPreviewFromQuery,
  stripAdminPreviewTokenFromUrl,
} from '../utils/courses-admin-preview-url.utils'
import { useCoursesAdminPreview } from '../composables/useCoursesAdminPreview'
import { useCoursesAdminPreviewStore } from '../stores/courses-admin-preview.ui-store'

import type { Pinia } from 'pinia'

/**
 * Courses admin-preview session hydration + IAM v2 token exchange (clientportal).
 *
 * `00.` prefix runs this before `auth.global` and `courses-access-guard.global` so they see a hydrated preview session.
 * Scope: TOP-LEVEL `/courses` only. On a fresh, context-gated entry with a `v2.` client-portal magic-link token, the
 * token is exchanged for an IAM v2 admin session via `POST /user/v2/login-token`, persisted in `sessionStorage`
 * (`acatv2`) so client-side `BaseService` calls authenticate as the admin. `v2.` only selects the exchange path — the
 * admin-preview credential (context gate) is what authorises it.
 *
 * Session lives in `sessionStorage` (client-only) because the IAM v2 JWT envelope is too large for a cookie, so there
 * is no SSR admin-auth path (SSR falls back to the learner flow).
 */
export default defineNuxtRouteMiddleware(async to => {
  const nuxtApp = useNuxtApp()
  const pinia = nuxtApp.$pinia as Pinia | undefined
  if (!pinia) {
    return
  }

  setActivePinia(pinia)

  const store = useCoursesAdminPreviewStore(pinia)

  // Leaving /courses ends the preview session (scope isolation); falls back to normal auth.
  if (!isCoursesAdminPreviewRoute(to.path)) {
    if (store.isAdminPreview || store.adminSession) {
      store.clearAdminPreview()
      clearAdminV2PreviewEnvelope()
      clearAdminPreviewAgencyToken()
      clearAdminPreviewMarkerCookie()
    }
    return
  }

  // Fresh admin-preview entry (token + location_id + is_preview in the URL): hydrate context. Once the
  // token is stripped, subsequent /courses navigations carry no credential and reuse the in-memory session.
  const credential = parseCoursesAdminPreviewFromQuery(to.query)
  if (!credential) {
    // Reload / post-token-strip: no token in the URL, but `acatv2` may still be in sessionStorage
    // (client-only, survives reload). Re-hydrate the store without another exchange. No-op on SSR.
    if (process.client && !store.adminSession) {
      const envelope = readAdminV2PreviewEnvelope()
      const sessionLocationId = getAdminV2PreviewLocationId(envelope)
      if (sessionLocationId) {
        const context = parseCoursesAdminPreviewContextFromQuery(to.query)
        store.setAdminPreviewContext({
          locationId: sessionLocationId,
          builder: context?.builder ?? false,
          templateId: context?.templateId ?? null,
          companyId: context?.companyId ?? null,
        })
        store.setAdminSession(envelope)
        // Restore the raw agency token so the federated remote can re-run its sign-in exchange on
        // reload (it can't authenticate with the exchanged admin access token).
        const agencyToken = readAdminPreviewAgencyToken()
        if (agencyToken) {
          store.setAdminPreviewToken(agencyToken)
        }
      }
    }

    // Internal remote/host navigation may construct a new query instead of carrying the current
    // preview context. Restore only the non-sensitive allowlisted keys while the per-tab admin
    // session is active so `company_id` remains available to the remote's reload exchange.
    if (process.client && store.isAdminPreview && store.previewLocationId) {
      const mergedQuery = mergeCoursesAdminPreviewQuery(to.query, {
        locationId: store.previewLocationId,
        builder: store.builderMode,
        templateId: store.templateId,
        companyId: store.companyId,
      })
      const queryChanged = [...new Set([...Object.keys(to.query), ...Object.keys(mergedQuery)])].some(
        key => JSON.stringify(mergedQuery[key]) !== JSON.stringify(to.query[key])
      )
      if (queryChanged) {
        return navigateTo({ path: to.path, query: mergedQuery, hash: to.hash }, { replace: true })
      }
    }
    return
  }

  store.setAdminPreviewContext({
    locationId: credential.locationId,
    builder: credential.builder,
    templateId: credential.templateId,
    companyId: credential.companyId,
  })

  // Everything below is client-only: the agency Bearer must never enter server-side Pinia state
  // (it would be serialized into the SSR payload) — the exchanged session lives in sessionStorage.
  if (!process.client) {
    return
  }

  // Capture the agency Bearer for the federated remote's own sign-in exchange (it can't authenticate
  // with the exchanged admin access token). Always overwrite: a full credential is a fresh entry,
  // possibly a same-tab switch to a DIFFERENT preview — not overwriting would leave the remote
  // authenticating as the previous preview's agency.
  store.setAdminPreviewToken(credential.token)

  // IAM v2 admin preview: exchange the `v2.` builder token for an admin session on every fresh
  // full credential (builder re-entry / theme switch). A full credential only appears on a genuine
  // builder entry — the token is stripped from the URL on every later navigation/reload, so this
  // block is not reached again for the same entry (no duplicate exchange). Re-exchanging is required
  // so a switch that carries a new `company_id` forwards it to `/login-token`. Non-`v2.` (legacy)
  // credentials are left unchanged (no v1 exchange path).
  if (isV2PreviewToken(credential.token)) {
    const { exchangeAdminPreviewSession } = useCoursesAdminPreview()
    await exchangeAdminPreviewSession(credential.token, credential.locationId, credential.companyId)
  }

  // Scrub the credential from the address bar via router replace, preserving other params
  // (is_preview/location_id/builder/template_id/company_id survive for reload). Re-enters this middleware
  // without a credential and passes through on the already-hydrated store/session.
  const strippedUrl = stripAdminPreviewTokenFromUrl(to.fullPath)
  if (strippedUrl !== to.fullPath) {
    return navigateTo(strippedUrl, { replace: true })
  }
})
